import { WSPillProps, WSText } from "@wingspanhq/fe-component-library";
import {
  OrganizationSsoConfigStatus,
  PersonMfaFactor
} from "@wingspanhq/wingspan-api-sdk";
import { queriesMfa } from "@query";
import { useSession } from "@hooks/useSession";
import React from "react";
import { DataView, DataItemProps } from "../../../shared/components/DataView";
import { FormDataPhoneNumber } from "../../../modules/Onboarding/components/FormPartialPhoneNumber";
import { ENROLLABLE_FACTOR_TYPES, mfaFactorTypeName } from "./mfaFactorEnroll";
import { useModalAddMfaFactor } from "./ModalAddMfaFactor";
import { useModalRemoveMfaFactor } from "./ModalRemoveMfaFactor";

type Props = {
  defaultEmail?: string;
  defaultPhone?: FormDataPhoneNumber;
};

const statusPill = (
  status: OrganizationSsoConfigStatus
): WSPillProps | undefined => {
  switch (status) {
    case OrganizationSsoConfigStatus.ACTIVE:
      return { theme: "success", text: "Active" };
    case OrganizationSsoConfigStatus.PENDING_VERIFICATION:
      return { theme: "warning", text: "Pending verification" };
    case OrganizationSsoConfigStatus.DISABLED:
      return { theme: "neutral", text: "Disabled" };
    default:
      return undefined;
  }
};

export const MfaFactorsSection: React.FC<Props> = props => {
  const { personId } = useSession();
  const modalAdd = useModalAddMfaFactor();
  const modalRemove = useModalRemoveMfaFactor();

  const factorsQuery = queriesMfa.listPersonMfaFactors.useQuery({ personId });

  const factors = (factorsQuery.data?.data ?? []).filter(factor =>
    ENROLLABLE_FACTOR_TYPES.includes(factor.type)
  );

  const rows: DataItemProps[] = factors.map((factor: PersonMfaFactor) => ({
    label: { text: mfaFactorTypeName(factor.type) },
    value: factor.label,
    pill: statusPill(factor.status),
    action: {
      label: "Remove",
      onClick: () => modalRemove.open({ factor })
    }
  }));

  return (
    <DataView
      header={{
        label: {
          text: "Security",
          helperText:
            "Add an extra layer of protection with 2-step verification."
        },
        value: {
          action: {
            label: "Add",
            onClick: () =>
              modalAdd.open({
                defaultEmail: props.defaultEmail,
                defaultPhone: props.defaultPhone
              })
          }
        }
      }}
      loading={factorsQuery.isLoading}
      data={rows}
    >
      {!factorsQuery.isLoading && factors.length === 0 ? (
        <WSText color="gray500">
          You haven't added any verification methods yet.
        </WSText>
      ) : null}
    </DataView>
  );
};
